import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Switch,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import adminService from '../../services/adminService';
import { getApiErrorInfo } from '../../services/api';

export default function MemoryMigration() {
  const [sourceReasoningEngineId, setSourceReasoningEngineId] = useState('');
  const [targetReasoningEngineId, setTargetReasoningEngineId] = useState('');
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ copiedCount: number; message: string } | null>(null);
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof adminService.previewMemoryMigration>> | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const canSubmit = sourceReasoningEngineId.trim().length > 0 && targetReasoningEngineId.trim().length > 0 && sourceReasoningEngineId.trim() !== targetReasoningEngineId.trim();

  const migrationRequest = {
    sourceReasoningEngineId: sourceReasoningEngineId.trim(),
    targetReasoningEngineId: targetReasoningEngineId.trim(),
    skipDuplicates,
  };

  async function handlePreview() {
    setPreviewLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await adminService.previewMemoryMigration(migrationRequest);
      setPreview(response);
      setConfirmOpen(true);
    } catch (err: unknown) {
      const apiError = getApiErrorInfo(err);
      setError(apiError.message || 'Preview failed.');
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleMigrate() {
    setMigrationLoading(true);
    setError(null);
    setResult(null);
    setConfirmOpen(false);

    try {
      const response = await adminService.migrateAllMemories(migrationRequest);

      setResult({ copiedCount: response.copiedCount, message: response.message });
    } catch (err: unknown) {
      const apiError = getApiErrorInfo(err);
      setError(apiError.message || 'Memory migration failed.');
    } finally {
      setMigrationLoading(false);
    }
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Copy every memory entry from one Vertex AI reasoning engine into another. This preserves each memory&apos;s user scope and is intended for engine migrations, not routine syncs.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'warning.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <WarningAmberIcon color="warning" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
              Migration impact
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This copies all memories from the source engine into the target engine, across every user scope found in the source. Keep the target engine id correct before running it.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <CloudSyncIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Migrate Memories
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2.5 }}>
          <TextField
            label="Source reasoning engine id"
            value={sourceReasoningEngineId}
            onChange={(e) => setSourceReasoningEngineId(e.target.value)}
            fullWidth
            placeholder="old-agent-engine"
            helperText="Reads all memories from this engine."
          />

          <TextField
            label="Target reasoning engine id"
            value={targetReasoningEngineId}
            onChange={(e) => setTargetReasoningEngineId(e.target.value)}
            fullWidth
            placeholder="new-agent-engine"
            helperText="Writes copied memories here with the original user_id scope."
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Switch checked={skipDuplicates} onChange={(e) => setSkipDuplicates(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>Skip duplicate facts</Typography>
                <Typography variant="caption" color="text.secondary">Prevents rewriting memories that already exist in the target engine.</Typography>
              </Box>
            }
          />
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip icon={<ContentCopyIcon />} label="All users" variant="outlined" />
            <Chip label={skipDuplicates ? 'Duplicate skip on' : 'Duplicate skip off'} color={skipDuplicates ? 'success' : 'default'} variant="outlined" />
          </Box>

          <Button
            variant="contained"
            onClick={handlePreview}
            disabled={previewLoading || migrationLoading || !canSubmit}
            startIcon={previewLoading ? <CircularProgress size={18} color="inherit" /> : <CloudSyncIcon />}
          >
            {previewLoading ? 'Preparing preview…' : 'Preview Migration'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Paper variant="outlined" sx={{ p: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1" fontWeight={600}>
            {result.message}
          </Typography>
          <Chip label={`${result.copiedCount} memories copied`} color="success" size="small" />
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Confirm memory migration</DialogTitle>
        <DialogContent dividers>
          {preview && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="warning">
                This will copy all memories from the source engine into the target engine. Review the preview below before continuing.
              </Alert>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Source facts</Typography>
                  <Typography variant="h5" fontWeight={600}>{preview.sourceFactCount}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Target facts</Typography>
                  <Typography variant="h5" fontWeight={600}>{preview.targetFactCount}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Source users</Typography>
                  <Typography variant="h5" fontWeight={600}>{preview.sourceUserCount}</Typography>
                </Paper>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Sample memories</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {preview.previewSample.map((item, index) => (
                    <Paper key={`${item.userId ?? 'unknown'}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        User: {item.userId || 'unknown'}
                      </Typography>
                      <Typography variant="body2">{item.fact}</Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={migrationLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleMigrate}
            disabled={migrationLoading}
            startIcon={migrationLoading ? <CircularProgress size={18} color="inherit" /> : <CloudSyncIcon />}
          >
            {migrationLoading ? 'Migrating…' : 'Confirm & Migrate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}